import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { supabase } from '../services/supabase'

interface AdminUser {
    id: string
    email: string
    nome: string
    role: 'super_admin' | 'admin'
    created_at: string
    last_login: string | null
}

interface AdminAuthContextType {
    admin: AdminUser | null
    loading: boolean
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
    logout: () => Promise<void>
    isAuthenticated: boolean
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

interface AdminAuthProviderProps {
    children: ReactNode
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
    const [admin, setAdmin] = useState<AdminUser | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        checkAdminSession()
    }, [])

    const checkAdminSession = async () => {
        try {
            const { data: { session } } = await supabase.auth.getSession()

            if (session?.user) {
                // Verificar se o usuário é admin
                const { data: adminData, error } = await supabase
                    .from('admin_users')
                    .select('*')
                    .eq('email', session.user.email)
                    .eq('ativo', true)
                    .single()

                if (adminData && !error) {
                    setAdmin(adminData)
                } else {
                    // Usuário não é admin - apenas não definir como admin
                    // NÃO fazer logout aqui, pois pode ser um usuário comum logado
                    setAdmin(null)
                }
            } else {
                // Sem sessão, garantir que admin está null
                setAdmin(null)
            }
        } catch (error) {
            console.error('Erro ao verificar sessão admin:', error)
            setAdmin(null)
        } finally {
            setLoading(false)
        }
    }

    const login = async (email: string, password: string) => {
        try {
            setLoading(true)

            // Fazer login no Supabase
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            })

            if (error) {
                return { success: false, error: error.message }
            }

            if (data.user) {
                console.log('🔍 Usuário autenticado:', data.user.email)

                // Verificar se é admin
                const { data: adminData, error: adminError } = await supabase
                    .from('admin_users')
                    .select('*')
                    .eq('email', email)
                    .eq('ativo', true)
                    .single()

                console.log('🔍 Resultado consulta admin_users:', { adminData, adminError })

                if (adminError || !adminData) {
                    console.log('❌ Erro ou usuário não encontrado:', adminError)
                    await supabase.auth.signOut()
                    return { success: false, error: 'Acesso negado. Usuário não é administrador.' }
                }

                // Atualizar último login
                await supabase
                    .from('admin_users')
                    .update({ last_login: new Date().toISOString() })
                    .eq('id', adminData.id)

                setAdmin(adminData)
                return { success: true }
            }

            return { success: false, error: 'Erro no login' }
        } catch (error) {
            return { success: false, error: 'Erro interno do servidor' }
        } finally {
            setLoading(false)
        }
    }

    const logout = async () => {
        try {
            await supabase.auth.signOut()
            setAdmin(null)
        } catch (error) {
            console.error('Erro ao fazer logout:', error)
        }
    }

    const value = {
        admin,
        loading,
        login,
        logout,
        isAuthenticated: !!admin
    }

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    )
}

export function useAdminAuth() {
    const context = useContext(AdminAuthContext)
    if (context === undefined) {
        throw new Error('useAdminAuth deve ser usado dentro de um AdminAuthProvider')
    }
    return context
}
